import {useCallback, useEffect, useRef} from 'react';
import {type IAsset} from '@netx/core-asset-next/schema';
// import {currentCart} from '@netx/core-cart/singletons/currentCart';
import {useRefState} from '@netx/core-components/hooks/useRefState';

export type UseCartAssets = {
	hasAssets: boolean;
	assets: IAsset[];
	assetCount: number;
	queued: boolean;
	lastUpdate: number;
};

export const useCurrentCart = () => {
	const [cart, cartRef, setCart] = useRefState<UseCartAssets>({
		hasAssets: false,
		assets: [],
		assetCount: 0,
		queued: false,
		lastUpdate: 0,
	});

	const updateTimeout = useRef<NodeJS.Timeout>();

	const clear = useCallback(() => Netx.cart.current.empty(), []);

	const update = useCallback(() => {
		const total = Netx.cart.current.getTotal();
		setCart((currentState) => ({
			...currentState,
			hasAssets: total > 0,
			assets: Netx.cart.current.models.map((model) => model.attributes),
			assetCount: total,
			lastUpdate:
				total !== currentState.assetCount
					? Date.now()
					: currentState.lastUpdate,
			queued:
				!!Netx.cart.current.addQueue.assets.length ||
				!!Netx.cart.current.removeQueue.assets.length,
		}));
	}, []);

	useEffect(() => {
		const listener = () => {
			updateTimeout.current && clearTimeout(updateTimeout.current);
			updateTimeout.current = setTimeout(() => update(), 200);
		};
		Netx.cart.current.on('add remove reset sync', listener);
		Netx.cart.current.addQueue.assets.on('add remove', listener);
		Netx.cart.current.removeQueue.assets.on('add remove', listener);

		update();

		return () => {
			updateTimeout.current && clearTimeout(updateTimeout.current);
			Netx.cart.current.off('add remove reset sync', listener);
			Netx.cart.current.addQueue.assets.off('add remove', listener);
			Netx.cart.current.removeQueue.assets.off('add remove', listener);
		};
	}, []);

	return {cart, clear, cartRef};
};

export default useCurrentCart;
